import { Minus, Plus, Trash2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { ApiError } from '../api/http.js';
import { useCatalog, useCheckout } from '../api/queries.js';
import type { KitOffer } from '../api/types.js';
import { useCart } from '../cart/cart-context.js';
import { EmptyState } from '../components/empty-state.js';
import { ErrorState } from '../components/error-state.js';
import { PageHeader } from '../components/page-header.js';
import { formatPrice } from '../lib/format.js';

const CHECKOUT_ERRORS: Readonly<Record<string, string>> = {
  INSUFFICIENT_STOCK:
    'Ya no queda stock suficiente de algún kit. Ajusta las cantidades e inténtalo de nuevo.',
  PRICE_UNAVAILABLE:
    'Los precios de mercado no están disponibles en este momento. Inténtalo en unos segundos.',
  PAYMENT_INIT_FAILED: 'La pasarela de pago no respondió. Inténtalo de nuevo.',
  INVALID_CHECKOUT: 'Revisa tu nombre y correo.',
};

interface CartLine {
  readonly kit: KitOffer;
  readonly quantity: number;
}

function QuantityStepper({ line }: { line: CartLine }) {
  const cart = useCart();
  const setTo = (quantity: number) => {
    cart.setQuantity(line.kit.sku, Math.max(1, Math.min(line.kit.stock, quantity)));
  };
  const buttonClass =
    'flex size-11 cursor-pointer items-center justify-center rounded-control border border-border hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40';
  return (
    <div
      className="flex items-center gap-2"
      role="group"
      aria-label={`Cantidad de ${line.kit.name}`}
    >
      <button
        type="button"
        className={buttonClass}
        disabled={line.quantity <= 1}
        onClick={() => {
          setTo(line.quantity - 1);
        }}
        aria-label="Quitar uno"
      >
        <Minus aria-hidden="true" className="size-4" />
      </button>
      <span className="w-8 text-center font-medium" aria-live="polite">
        {line.quantity}
      </span>
      <button
        type="button"
        className={buttonClass}
        disabled={line.quantity >= line.kit.stock}
        onClick={() => {
          setTo(line.quantity + 1);
        }}
        aria-label="Agregar uno"
      >
        <Plus aria-hidden="true" className="size-4" />
      </button>
    </div>
  );
}

function CheckoutForm({ lines }: { lines: CartLine[] }) {
  const checkout = useCheckout();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    checkout.mutate(
      {
        items: lines.map((line) => ({ sku: line.kit.sku, quantity: line.quantity })),
        buyer: { name, email },
      },
      {
        onSuccess: (result) => {
          window.location.assign(result.redirectUrl);
        },
      },
    );
  };
  const errorCode = checkout.error instanceof ApiError ? checkout.error.code : undefined;
  const inputClass = 'min-h-11 rounded-control border border-border bg-card px-3';
  return (
    <form
      onSubmit={submit}
      className="flex flex-col gap-4 rounded-card border border-border bg-card p-5"
    >
      <h2 className="text-lg font-semibold">Datos del comprador</h2>
      <label className="flex flex-col gap-1">
        <span className="font-medium">Nombre o razón social</span>
        <input
          required
          maxLength={100}
          value={name}
          onChange={(event) => {
            setName(event.target.value);
          }}
          autoComplete="name"
          className={inputClass}
        />
      </label>
      <label className="flex flex-col gap-1">
        <span className="font-medium">Correo electrónico</span>
        <input
          required
          type="email"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
          autoComplete="email"
          className={inputClass}
        />
      </label>
      {checkout.isError && (
        <p role="alert" className="text-destructive">
          {CHECKOUT_ERRORS[errorCode ?? ''] ?? 'No pudimos iniciar el pago. Inténtalo de nuevo.'}
        </p>
      )}
      <button
        type="submit"
        disabled={checkout.isPending || checkout.isSuccess}
        className="min-h-11 cursor-pointer rounded-control bg-primary px-5 font-medium text-on-primary transition-opacity duration-150 hover:opacity-90 disabled:cursor-wait disabled:opacity-60"
      >
        {checkout.isPending || checkout.isSuccess ? 'Redirigiendo al pago…' : 'Pagar'}
      </button>
      <p className="text-sm text-muted-foreground">
        Serás redirigido a Integrapay. Tu stock queda reservado por 6 minutos.
      </p>
    </form>
  );
}

export function CartPage() {
  const cart = useCart();
  const catalog = useCatalog();
  const lines: CartLine[] = (catalog.data?.kits ?? []).flatMap((kit) => {
    const quantity = cart.quantities[kit.sku];
    return quantity === undefined ? [] : [{ kit, quantity: Math.min(quantity, kit.stock) }];
  });
  const priced = lines.every((line) => line.kit.price !== null);
  const total = lines.reduce((sum, line) => sum + line.quantity * (line.kit.price ?? 0), 0);

  if (catalog.isPending) return <PageHeader title="Carro de compras" description="Cargando…" />;
  if (catalog.isError)
    return (
      <ErrorState
        message="No pudimos cargar precios y stock."
        onRetry={() => void catalog.refetch()}
      />
    );
  return (
    <>
      <PageHeader
        title="Carro de compras"
        description="Revisa cantidades y el total antes de pagar. Los precios son los vigentes del mercado."
      />
      {lines.length === 0 ? (
        <EmptyState message="Tu carro está vacío." />
      ) : (
        <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
          <section aria-label="Kits en el carro" className="flex flex-col gap-3">
            {lines.map((line) => (
              <article
                key={line.kit.sku}
                className="flex flex-wrap items-center justify-between gap-4 rounded-card border border-border bg-card p-4"
              >
                <div className="min-w-0">
                  <h2 className="font-semibold">{line.kit.name}</h2>
                  <p className="text-sm text-muted-foreground">
                    {line.kit.price === null
                      ? 'Precio no disponible'
                      : `${formatPrice(line.kit.price)} c/u`}{' '}
                    · {line.kit.stock} en stock
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <QuantityStepper line={line} />
                  <p className="w-28 text-right font-semibold">
                    {line.kit.price === null ? '—' : formatPrice(line.quantity * line.kit.price)}
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      cart.remove(line.kit.sku);
                    }}
                    aria-label={`Eliminar ${line.kit.name}`}
                    className="flex size-11 cursor-pointer items-center justify-center rounded-control text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 aria-hidden="true" className="size-5" />
                  </button>
                </div>
              </article>
            ))}
            <p className="text-right text-xl font-bold">Total: {formatPrice(total)}</p>
          </section>
          {priced ? (
            <CheckoutForm lines={lines} />
          ) : (
            <ErrorState message="Falta el precio vigente de algún kit; espera unos segundos." />
          )}
        </div>
      )}
    </>
  );
}
